import { Loadable } from '@/components/loaders';
import { Button } from '@/lib';
import { PerformanceAudienceChartMetric } from '@/services/endpoints/performanceAudience';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PerformanceDashboardView } from './components/PerformanceDashboardView';
import { usePerformanceDashboardData } from './hooks/usePerformanceDashboardData';
import { PerformanceAudienceFiltersState } from './types';
import { PerformanceAudienceDashboardEmpty } from './components';

type PerformanceDashboardContainerProps = {
  filters: PerformanceAudienceFiltersState;
  enabled?: boolean;
};

export const PerformanceDashboardContainer: FC<PerformanceDashboardContainerProps> = ({ filters, enabled = true }) => {
  const { t } = useTranslation('performanceAudience');
  const [activeMetric, setActiveMetric] = useState<PerformanceAudienceChartMetric>('impressions');

  const { summary, kpis, trend, comparison, storeVisits, isLoading, isError, refetch, hasData } = usePerformanceDashboardData(filters, enabled);

  const isDashboardEmpty = useMemo(() => {
    return !summary && !kpis.length && !trend && !comparison.length && !storeVisits;
  }, [summary, kpis, trend, comparison, storeVisits]);

  const handleExportCsv = useCallback(() => {
    // Placeholder for comparison table CSV export
  }, []);

  const handleEditScope = useCallback(() => {
    // Placeholder for scope editor navigation
  }, []);

  if (isLoading) {
    // Render loading state skeleton for the dashboard
    return (
      <div className='grid grid-cols-1 gap-6'>
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
        <div className='h-10 w-full bg-gray-200 animate-pulse rounded-md' />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800'>
        <p>{t('errors.loadFailed')}</p>
        <Button intent='secondary' size='sm' className='mt-4' onClick={() => refetch()}>
          {t('errors.retry')}
        </Button>
      </div>
    );
  }

  if (isDashboardEmpty) {
    return <PerformanceAudienceDashboardEmpty />;
  }

  return (
    <Loadable isLoading={isLoading}>
      {hasData ? (
        <PerformanceDashboardView
          summary={summary}
          kpis={kpis}
          trend={trend}
          comparison={comparison}
          storeVisits={storeVisits}
          dateRange={filters.dateRange}
          activeMetric={activeMetric}
          onMetricChange={setActiveMetric}
          onEditScope={handleEditScope}
          onExportCsv={handleExportCsv}
        />
      ) : null}
    </Loadable>
  );
};
